import { useRef, useState, type ReactNode } from "react";
import { hasPermission, PERMISSIONS, useSession } from "@/modules/auth/useSession";
import {
  APPROVAL_REQUIRED_CODE,
  isApprovalRefusal,
  type CommandApproval,
  type InvoiceCommand,
} from "@/shared/invoices/invoiceCommands";
import { ManagerApprovalSheet } from "./components/ManagerApprovalSheet";
import {
  approvalRequiredFor,
  commandNeedsApproval,
  type ApprovalCommandType,
} from "./lib/managerApproval";

/**
 * Sends ONE command, with the approval when there is one. Must THROW an
 * approval refusal (`RESTAURANT_APPROVAL_*`) rather than swallow it, so the
 * sheet can stay open on a wrong password; any other failure is the caller's
 * to report, and comes back as `null`.
 */
export type ApprovalSender<R> = (
  approval: CommandApproval | undefined,
) => Promise<R | null>;

type Pending = {
  commandType: ApprovalCommandType;
  submit: (approval: CommandApproval) => Promise<void>;
  cancel: () => void;
};

/**
 * D156 — THE APPROVAL GATE, for every till control that sends a covered act
 * (the void dialog, the comp dialog, the discount sheet, the staff meal's comp
 * step, the refund sheet when stock comes back).
 *
 * `gate(command, send)` sends straight through when no password is needed —
 * the store's switch is off, the act is not covered, or this session holds
 * `RESTAURANT_EXCEPTION_APPROVE`. Otherwise it opens «اعتماد المدير» FIRST and
 * sends only once a password is typed; the promise resolves with the send's
 * result, or `null` when the sheet is cancelled. If the server asks when the
 * till thought it would not (the owner switched it on a moment ago), the sheet
 * opens then.
 *
 * The approval is for that ONE send and is never kept: the next act asks
 * again. Render `sheet` once beside the controls.
 */
export function useManagerApproval({ switchOn }: { switchOn: boolean }): {
  gate: <R>(command: InvoiceCommand, send: ApprovalSender<R>) => Promise<R | null>;
  sheet: ReactNode;
  isRequired: (command: InvoiceCommand) => boolean;
} {
  const { data: session } = useSession();
  const holdsApprove = hasPermission(session, PERMISSIONS.EXCEPTION_APPROVE);
  const [pending, setPending] = useState<Pending | null>(null);
  // One sheet at a time: a second gated tap while one is open waits its turn
  // by being refused (it resolves null) rather than replacing the first.
  const openRef = useRef(false);

  // A type guard: `approvalRequiredFor` is true only for a covered act, so a
  // `true` here also narrows the command to an `ApprovalCommandType`.
  const isRequired = (
    command: InvoiceCommand,
  ): command is InvoiceCommand & { type: ApprovalCommandType } =>
    approvalRequiredFor({
      switchOn,
      holdsApprove,
      type: command.type,
      payload: command.payload,
    });

  const ask = <R,>(
    commandType: ApprovalCommandType,
    send: ApprovalSender<R>,
  ): Promise<R | null> => {
    if (openRef.current) return Promise.resolve(null);
    openRef.current = true;
    return new Promise<R | null>((resolve) => {
      const finish = (value: R | null) => {
        openRef.current = false;
        setPending(null);
        resolve(value);
      };
      setPending({
        commandType,
        cancel: () => finish(null),
        submit: async (approval) => {
          // An approval refusal propagates to the sheet, which stays open.
          const result = await send(approval);
          finish(result);
        },
      });
    });
  };

  const gate = async <R,>(
    command: InvoiceCommand,
    send: ApprovalSender<R>,
  ): Promise<R | null> => {
    if (isRequired(command)) {
      return ask(command.type, send);
    }
    try {
      return await send(undefined);
    } catch (failure) {
      if (
        isApprovalRefusal(failure) &&
        failure.code === APPROVAL_REQUIRED_CODE &&
        commandNeedsApproval(command.type, command.payload)
      ) {
        return ask(command.type, send);
      }
      throw failure;
    }
  };

  const sheet = pending ? (
    <ManagerApprovalSheet
      commandType={pending.commandType}
      onCancel={pending.cancel}
      onSubmit={pending.submit}
    />
  ) : null;

  return { gate, sheet, isRequired };
}
